import { useState, useCallback, useMemo } from 'react';
import type {
  PoleImageType,
  PoleAttachmentType,
  PoleFormatOption,
  TempReferencePoints,
} from '../types';

/**
 * Hook configuration options
 */
export interface UsePoleCanvasOptions {
  /** Initial pole image */
  initialImage?: PoleImageType;
  /** Initial attachments */
  initialAttachments?: PoleAttachmentType[];
  /** Initial measurement format */
  initialFormat?: PoleFormatOption;
  /** Callback when data changes */
  onChange?: (data: PoleCanvasState) => void;
}

/**
 * State managed by the hook
 */
export interface PoleCanvasState {
  poleImage: PoleImageType | null;
  attachments: PoleAttachmentType[];
  measurementFormat: PoleFormatOption;
  selectedAttachmentId: string | null;
  isAddMode: boolean;
  isReferenceLineUpdateMode: boolean;
  tempReferencePoints: TempReferencePoints;
}

/**
 * Actions returned by the hook
 */
export interface PoleCanvasActions {
  setPoleImage: (image: PoleImageType) => void;
  setAttachments: (attachments: PoleAttachmentType[]) => void;
  addAttachment: (attachment: PoleAttachmentType) => void;
  updateAttachment: (id: string, updates: Partial<PoleAttachmentType>) => void;
  removeAttachment: (id: string) => void;
  selectAttachment: (id: string | null) => void;
  setMeasurementFormat: (format: PoleFormatOption) => void;
  toggleAddMode: () => void;
  toggleReferenceLineUpdateMode: () => void;
  updateReferencePoint: (point: 'top' | 'bottom', x: number, y: number) => void;
  commitReferencePoints: () => void;
  cancelReferencePointUpdate: () => void;
  updateAttachmentPosition: (attachmentId: string, imageId: string, yPixel: number) => void;
}

/**
 * Main hook for managing pole canvas state
 */
export function usePoleCanvas(
  options: UsePoleCanvasOptions = {}
): [PoleCanvasState, PoleCanvasActions] {
  const {
    initialImage = null,
    initialAttachments = [],
    initialFormat = 'both',
    onChange,
  } = options;

  // Core state
  const [poleImage, setPoleImageState] = useState<PoleImageType | null>(initialImage);
  const [attachments, setAttachmentsState] = useState<PoleAttachmentType[]>(initialAttachments);
  const [measurementFormat, setMeasurementFormat] = useState<PoleFormatOption>(initialFormat);
  const [selectedAttachmentId, setSelectedAttachmentId] = useState<string | null>(null);
  const [isAddMode, setIsAddMode] = useState(false);
  const [isReferenceLineUpdateMode, setIsReferenceLineUpdateMode] = useState(false);
  const [tempReferencePoints, setTempReferencePoints] = useState<TempReferencePoints>({});

  // State object
  const state: PoleCanvasState = useMemo(() => ({
    poleImage,
    attachments,
    measurementFormat,
    selectedAttachmentId,
    isAddMode,
    isReferenceLineUpdateMode,
    tempReferencePoints,
  }), [
    poleImage,
    attachments,
    measurementFormat,
    selectedAttachmentId,
    isAddMode,
    isReferenceLineUpdateMode,
    tempReferencePoints,
  ]);

  // Notify changes
  const notifyChange = useCallback(() => {
    onChange?.(state);
  }, [onChange, state]);

  // Action: Set pole image
  const setPoleImage = useCallback((image: PoleImageType) => {
    setPoleImageState(image);
    notifyChange();
  }, [notifyChange]);

  // Action: Set attachments
  const setAttachments = useCallback((newAttachments: PoleAttachmentType[]) => {
    setAttachmentsState(newAttachments);
    notifyChange();
  }, [notifyChange]);

  // Action: Add attachment
  const addAttachment = useCallback((attachment: PoleAttachmentType) => {
    setAttachmentsState(prev => [...prev, attachment]);
    notifyChange();
  }, [notifyChange]);

  // Action: Update attachment
  const updateAttachment = useCallback((id: string, updates: Partial<PoleAttachmentType>) => {
    setAttachmentsState(prev => 
      prev.map(att => att.id === id ? { ...att, ...updates } : att)
    );
    notifyChange();
  }, [notifyChange]);

  // Action: Remove attachment
  const removeAttachment = useCallback((id: string) => {
    setAttachmentsState(prev => prev.filter(att => att.id !== id));
    if (selectedAttachmentId === id) {
      setSelectedAttachmentId(null);
    }
    notifyChange();
  }, [selectedAttachmentId, notifyChange]);

  // Action: Select attachment
  const selectAttachment = useCallback((id: string | null) => {
    setSelectedAttachmentId(id);
  }, []);

  // Action: Toggle add mode
  const toggleAddMode = useCallback(() => {
    setIsAddMode(prev => !prev);
    if (isReferenceLineUpdateMode) {
      setIsReferenceLineUpdateMode(false);
      setTempReferencePoints({});
    }
  }, [isReferenceLineUpdateMode]);

  // Action: Toggle reference line update mode
  const toggleReferenceLineUpdateMode = useCallback(() => {
    setIsReferenceLineUpdateMode(prev => !prev);
    if (isAddMode) {
      setIsAddMode(false);
    }
    if (!isReferenceLineUpdateMode) {
      // Starting edit mode, copy current points
      if (poleImage && poleImage.referenceLine) {
        setTempReferencePoints({
          top: {
            x: poleImage.referenceLine.top.pixelCoordinates.x,
            y: poleImage.referenceLine.top.pixelCoordinates.y,
            worldCoordinates: poleImage.referenceLine.top.worldCoordinates,
          },
          bottom: {
            x: poleImage.referenceLine.bottom.pixelCoordinates.x,
            y: poleImage.referenceLine.bottom.pixelCoordinates.y,
            worldCoordinates: poleImage.referenceLine.bottom.worldCoordinates,
          },
        });
      }
    } else {
      // Ending edit mode, clear temp points
      setTempReferencePoints({});
    }
  }, [isAddMode, isReferenceLineUpdateMode, poleImage]);

  // Action: Update reference point
  const updateReferencePoint = useCallback((point: 'top' | 'bottom', x: number, y: number) => {
    setTempReferencePoints(prev => ({
      ...prev,
      [point]: {
        x,
        y,
        worldCoordinates: prev[point]?.worldCoordinates,
      },
    }));
  }, []);

  // Action: Commit reference points
  const commitReferencePoints = useCallback(() => {
    if (poleImage && poleImage.referenceLine && tempReferencePoints.top && tempReferencePoints.bottom) {
      const updatedImage: PoleImageType = {
        ...poleImage,
        referenceLine: {
          top: {
            ...poleImage.referenceLine.top,
            pixelCoordinates: {
              x: tempReferencePoints.top.x,
              y: tempReferencePoints.top.y,
            },
          },
          bottom: {
            ...poleImage.referenceLine.bottom,
            pixelCoordinates: {
              x: tempReferencePoints.bottom.x,
              y: tempReferencePoints.bottom.y,
            },
          },
        },
      };
      setPoleImageState(updatedImage);
      setTempReferencePoints({});
      setIsReferenceLineUpdateMode(false);
      notifyChange();
    }
  }, [poleImage, tempReferencePoints, notifyChange]);

  // Action: Cancel reference point update
  const cancelReferencePointUpdate = useCallback(() => {
    setTempReferencePoints({});
    setIsReferenceLineUpdateMode(false);
  }, []);

  // Action: Update attachment position
  const updateAttachmentPosition = useCallback((
    attachmentId: string,
    imageId: string,
    yPixel: number
  ) => {
    setAttachmentsState(prev => 
      prev.map(att => {
        if (att.id === attachmentId) {
          const imageIndex = att.images?.findIndex(img => img.imageId === imageId) ?? -1;
          if (imageIndex !== -1 && att.images) {
            const updatedImages = [...att.images];
            updatedImages[imageIndex] = {
              ...updatedImages[imageIndex],
              point: {
                ...updatedImages[imageIndex].point,
                y: yPixel,
              },
            };
            return { ...att, images: updatedImages };
          }
        }
        return att;
      })
    );
    notifyChange();
  }, [notifyChange]);

  // Actions object
  const actions: PoleCanvasActions = useMemo(() => ({
    setPoleImage,
    setAttachments,
    addAttachment,
    updateAttachment,
    removeAttachment,
    selectAttachment,
    setMeasurementFormat,
    toggleAddMode,
    toggleReferenceLineUpdateMode,
    updateReferencePoint,
    commitReferencePoints,
    cancelReferencePointUpdate,
    updateAttachmentPosition,
  }), [
    setPoleImage,
    setAttachments,
    addAttachment,
    updateAttachment,
    removeAttachment,
    selectAttachment,
    toggleAddMode,
    toggleReferenceLineUpdateMode,
    updateReferencePoint,
    commitReferencePoints,
    cancelReferencePointUpdate,
    updateAttachmentPosition,
  ]);

  return [state, actions];
}